import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Play, Pause, RotateCcw, Plus, Trash2, Timer } from 'lucide-react';

const STORAGE_KEY = 'kite-timer-templates';

const DEFAULT_TEMPLATES = [
  { id: 't1', name: '5 menit', seconds: 5 * 60 },
  { id: 't2', name: '2 menit', seconds: 2 * 60 },
  { id: 't3', name: '10 menit', seconds: 10 * 60 },
];

function useAudioBeep() {
  const audioContextRef = useRef(null);

  const getContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playBeep = useCallback((frequency = 800, duration = 0.08, type = 'sine') => {
    try {
      const ctx = getContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = frequency;
      osc.type = type;
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('Audio play failed:', e);
    }
  }, [getContext]);

  const playStart = useCallback(() => playBeep(880, 0.15), [playBeep]);
  const playTick = useCallback(() => playBeep(600, 0.05), [playBeep]);
  const playAlarm = useCallback(() => {
    playBeep(660, 0.3, 'square');
    setTimeout(() => playBeep(880, 0.3, 'square'), 400);
    setTimeout(() => playBeep(660, 0.3, 'square'), 800);
  }, [playBeep]);

  return { playStart, playTick, playAlarm };
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const TimerPanel = ({ open, onOpenChange }) => {
  const [templates, setTemplates] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_TEMPLATES;
      }
    } catch (e) {}
    return DEFAULT_TEMPLATES;
  });
  const [remaining, setRemaining] = useState(0);
  const [initialSeconds, setInitialSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [ended, setEnded] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateMinutes, setNewTemplateMinutes] = useState('1');

  const intervalRef = useRef(null);
  const { playStart, playTick, playAlarm } = useAudioBeep();

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    } catch (e) {}
  }, [templates]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopInterval();
  }, [stopInterval]);

  const startTimer = useCallback(
    (seconds) => {
      stopInterval();
      const sec = Math.max(1, Math.floor(Number(seconds) || 0));
      setInitialSeconds(sec);
      setRemaining(sec);
      setRunning(true);
      setEnded(false);
      playStart();
    },
    [stopInterval, playStart]
  );

  const pauseTimer = useCallback(() => {
    stopInterval();
    setRunning(false);
  }, [stopInterval]);

  const resetTimer = useCallback(() => {
    stopInterval();
    setRunning(false);
    setEnded(false);
    setRemaining(initialSeconds || 0);
  }, [stopInterval, initialSeconds]);

  useEffect(() => {
    if (!running) return;

    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        const next = prev - 1;
        if (next <= 10 && next >= 1) playTick();
        if (next <= 0) {
          stopInterval();
          setRunning(false);
          setEnded(true);
          playAlarm();
        }
        return Math.max(0, next);
      });
    }, 1000);

    return () => stopInterval();
  }, [running, playTick, playAlarm, stopInterval]);

  const addTemplate = (e) => {
    e.preventDefault();
    const name = (newTemplateName || '').trim() || `${newTemplateMinutes} menit`;
    const mins = Math.max(0.5, Math.min(120, parseFloat(newTemplateMinutes) || 1));
    const seconds = Math.round(mins * 60);
    const newT = {
      id: `custom-${Date.now()}`,
      name,
      seconds,
    };
    setTemplates((prev) => [...prev, newT]);
    setNewTemplateName('');
    setNewTemplateMinutes('1');
  };

  const removeTemplate = (id) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const isDefault = (id) => ['t1', 't2', 't3'].includes(id);

  const content = (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] sm:max-w-md max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => {
          if (running) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Timer Countdown
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Countdown display */}
          <div className="text-center py-4">
            <div
              className={`text-5xl sm:text-6xl font-mono font-bold tabular-nums ${
                ended ? 'text-red-600 animate-pulse' : remaining <= 10 && running ? 'text-amber-600' : 'text-slate-800'
              }`}
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {formatTime(remaining)}
            </div>
            {ended && (
              <p className="text-sm text-red-600 font-medium mt-2">Waktu habis!</p>
            )}
          </div>

          {/* Controls */}
          <div className="flex justify-center gap-3">
            {!running ? (
              <button
                type="button"
                onClick={() => {
                  const sec = remaining > 0 ? remaining : initialSeconds;
                  if (sec > 0) startTimer(sec);
                }}
                disabled={remaining <= 0 && initialSeconds <= 0}
                className="btn-primary min-h-[48px] px-6 flex items-center gap-2 touch-manipulation"
              >
                <Play size={20} />
                {remaining > 0 ? 'Lanjut' : 'Mulai'}
              </button>
            ) : (
              <button
                type="button"
                onClick={pauseTimer}
                className="btn-secondary min-h-[48px] px-6 flex items-center gap-2 touch-manipulation"
              >
                <Pause size={20} />
                Jeda
              </button>
            )}
            <button
              type="button"
              onClick={resetTimer}
              disabled={initialSeconds <= 0}
              className="btn-secondary min-h-[48px] px-4 flex items-center gap-2 touch-manipulation"
            >
              <RotateCcw size={18} />
              Reset
            </button>
          </div>

          {/* Templates */}
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Template</div>
            <div className="flex flex-wrap gap-2">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startTimer(t.seconds)}
                    disabled={running}
                    className="min-h-[44px] px-4 rounded-xl border-2 border-slate-200 bg-white text-slate-800 font-medium text-sm hover:border-sky-400 hover:bg-sky-50 transition-colors touch-manipulation disabled:opacity-50"
                  >
                    {t.name}
                  </button>
                  {!isDefault(t.id) && (
                    <button
                      type="button"
                      onClick={() => removeTemplate(t.id)}
                      className="p-2 text-slate-400 hover:text-red-600 rounded-lg"
                      title="Hapus template"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add template */}
          <div className="pt-3 border-t border-slate-200">
            <div className="text-sm font-semibold text-slate-700 mb-2">Tambah template</div>
            <form onSubmit={addTemplate} className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Nama (opsional)"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                className="flex-1 min-h-[44px] px-3 rounded-xl border border-slate-200 text-base"
              />
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0.5"
                  max="120"
                  step="0.5"
                  placeholder="Menit"
                  value={newTemplateMinutes}
                  onChange={(e) => setNewTemplateMinutes(e.target.value)}
                  className="w-24 min-h-[44px] px-3 rounded-xl border border-slate-200 text-base"
                />
                <button
                  type="submit"
                  className="btn-primary min-h-[44px] px-4 flex items-center gap-2 touch-manipulation"
                >
                  <Plus size={18} />
                  Tambah
                </button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return createPortal(content, document.body);
};

export default TimerPanel;
